import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import useSeo from "@/hooks/useSeo";
import { toast } from "sonner";

export default function EmailPreferences() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Email preferences — The Honest Donkey", description: "Manage your Honest Donkey email preferences.", path: "/email-preferences", noindex: true });

  useEffect(() => {
    if (!email || !token) { setError("This preferences link is invalid or incomplete."); return; }
    api.get("/email/preferences", { params: { email, token } })
      .then(({ data }) => setPrefs(data))
      .catch((e) => setError(e.response?.data?.detail || "This preferences link is invalid or expired."));
  }, [email, token]);

  const unsubscribe = async () => {
    setBusy(true);
    try {
      await api.post("/email/unsubscribe", { email, token });
      toast.success("You've been unsubscribed from marketing emails.");
      setPrefs((p) => ({ ...p, marketing_emails: false, suppressed: true }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update preferences."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-donkey-bg grain flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md card-donkey rounded-2xl p-8 text-center" data-testid="email-preferences">
        <DonkeyMascot size={88} alt="" className="mx-auto mb-4 opacity-95" />
        <h1 className="font-heading font-bold text-2xl text-donkey-warm">Email preferences</h1>
        {error && <p className="text-sm text-donkey-red mt-4" data-testid="prefs-error">{error}</p>}
        {prefs && !error && (
          <>
            <p className="text-sm text-donkey-grey mt-3">Preferences for <span className="text-donkey-warm">{prefs.email}</span></p>
            <div className="mt-6 p-4 rounded-xl border border-white/10 text-sm text-donkey-warm">
              Marketing emails: <span className={prefs.marketing_emails ? "text-donkey-green" : "text-donkey-grey"} data-testid="prefs-marketing-status">{prefs.marketing_emails ? "On" : "Off"}</span>
            </div>
            <p className="text-xs text-donkey-grey mt-3">Service emails about your account and any complimentary trial are always sent where necessary and are not affected by this setting.</p>
            {prefs.marketing_emails && (
              <Button onClick={unsubscribe} disabled={busy} className="mt-6 bg-donkey-orange text-donkey-black font-semibold w-full h-11" data-testid="prefs-unsubscribe">
                {busy ? "Updating…" : "Unsubscribe from marketing emails"}
              </Button>
            )}
            {!prefs.marketing_emails && <p className="text-sm text-donkey-green mt-6" data-testid="prefs-unsubscribed">You're unsubscribed from marketing emails.</p>}
          </>
        )}
      </div>
    </div>
  );
}
